import type { MenuProps } from '@mantine/core';
import type { CSSObject } from '@mantine/emotion';
import type { CSSProperties } from 'react';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import {
	DS_AI_ELEVATION,
	DS_AI_TRANSITION_PROPS,
} from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.tokens';
import { dsAiTypeStyle } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.typography';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';

const DROPDOWN_SURFACE = {
	border: 'none',
	borderRadius: '8px',
	boxShadow: DS_AI_ELEVATION.dropdown,
} satisfies CSSProperties;

export const DROPDOWN_MENU = {
	...DROPDOWN_SURFACE,
	padding: '4px',
} satisfies CSSProperties;

/** The outline stays transparent until the item is selected, so selecting shifts nothing. */
export const DROPDOWN_ITEM = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: '8px',
	minHeight: 36,
	padding: `0 8px`,
	border: '1px solid transparent',
	borderRadius: '4px',
	color: defaultTheme.v3.colors.dark[8],
	...dsAiTypeStyle('sans/14/400'),
} satisfies CSSProperties;

const DROPDOWN_ITEM_HOVER = {
	background: defaultTheme.v3.colors.gray[0],
} satisfies CSSProperties;

/** Selected item: tinted fill and accent outline, plus a trailing `Tick`. */
const DROPDOWN_ITEM_ACTIVE = {
	background: defaultTheme.v3.colors.blue[0],
	borderColor: defaultTheme.v3.colors.blue[6],
} satisfies CSSProperties;

/**
 * Select/Combobox option list as the DS menu. `&&` outranks the app theme's Select styles, which
 * Mantine inserts after these; Combobox's own p4 is kept, as it already matches.
 */
export const DROPDOWN_SELECT_STYLES = {
	dropdown: { '&&': DROPDOWN_SURFACE },
	option: {
		'&&': DROPDOWN_ITEM,
		'& + &': { marginTop: '4px' },
		'&&:hover, &&[data-combobox-selected]': DROPDOWN_ITEM_HOVER,
		// The theme bolds the checked option.
		'&&[data-checked]': { ...DROPDOWN_ITEM_ACTIVE, fontWeight: DROPDOWN_ITEM.fontWeight },
	},
} satisfies Record<string, CSSObject>;

/** Replaces the theme's `comboboxProps`, so restates its z-index (lists open above the drawer). */
export const DROPDOWN_COMBOBOX_PROPS = {
	zIndex: Z_INDEX.GLOBAL_MENU,
	offset: 4,
	transitionProps: DS_AI_TRANSITION_PROPS.menu,
} as const;

/** Mantine `Menu` as the DS menu; lifted above the modal and drawer layers. */
export const DS_AI_MENU_PROPS = {
	zIndex: Z_INDEX.GLOBAL_MENU,
	position: 'bottom-end',
	offset: 4,
	transitionProps: DS_AI_TRANSITION_PROPS.menu,
	styles: {
		dropdown: DROPDOWN_MENU,
		item: {
			minHeight: DROPDOWN_ITEM.minHeight,
			padding: '8px',
			borderRadius: DROPDOWN_ITEM.borderRadius,
			color: DROPDOWN_ITEM.color,
			fontSize: DROPDOWN_ITEM.fontSize,
			lineHeight: DROPDOWN_ITEM.lineHeight,
		},
		itemSection: { color: defaultTheme.v3.colors.dark[8] },
		divider: { margin: `4px 0`, borderColor: defaultTheme.v3.colors.gray[2] },
	},
} satisfies Partial<MenuProps>;
